import { useEffect } from "react";
import { HeroSection } from "@/components/HeroSection";
import { GrowthArchitectureDiagram } from "@/components/GrowthArchitectureDiagram";
import { MethodologySection } from "@/components/MethodologySection";
import { ExpertiseSection } from "@/components/ExpertiseSection";
import { FAQSection } from "@/components/FAQSection";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";

const Index = () => {
  useEffect(() => {
    // Keep the browser title concise; fuller identity stays in H1, metadata, and schema.
    document.title = "Kristina Shrider | National Growth Architect & Behavioral CMO";

    // Meta description
    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement;
    const originalDesc = metaDesc?.content;
    if (metaDesc) {
      metaDesc.content = "Kristina Shrider is a National Growth Architect, Behavioral CMO, and Independent AI Marketing Researcher. Founder of Market Disruptors AI Visibility Agency and creator of MAHI Index™, MAD-M™, and AIMRI research.";
    }

    // Canonical — update existing, never create duplicate
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    const origCanonical = canonical.href;
    canonical.href = "https://kristinashrider.com/";

    // AEO meta tags
    const setMeta = (attr: string, key: string, content: string) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
      el.content = content;
    };
    setMeta("property", "og:title", "Kristina Shrider | National Growth Architect & Behavioral CMO");
    setMeta("property", "og:description", "National Growth Architect, Behavioral CMO, and Independent AI Marketing Researcher. Founder of Market Disruptors AI Visibility Agency and creator of MAHI Index™, MAD-M™, and AIMRI research.");
    setMeta("property", "og:url", "https://kristinashrider.com/");
    setMeta("property", "og:image", "https://kristinashrider.com/kristina-shrider-executive-portrait.png");
    setMeta("property", "og:site_name", "Kristina Shrider — Independent Research & Practice");
    setMeta("property", "og:locale", "en_US");
    setMeta("property", "og:type", "profile");
    setMeta("property", "profile:first_name", "Kristina");
    setMeta("property", "profile:last_name", "Shrider");
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", "Kristina Shrider | National Growth Architect & Behavioral CMO");
    setMeta("name", "twitter:description", "National Growth Architect, Behavioral CMO, and Independent AI Marketing Researcher. Creator of MAHI Index™, MAD-M™, and AIMRI research.");
    setMeta("name", "twitter:image", "https://kristinashrider.com/kristina-shrider-executive-portrait.png");
    setMeta("name", "author", "Kristina Shrider");
    setMeta("name", "ai-description", "Kristina Shrider is a National Growth Architect, Behavioral CMO, and Independent AI Marketing Researcher. She is the founder of Market Disruptors AI Visibility Agency and the creator of the MAHI Index™ (Marketing Agent Health Index) and MAD-M™ (Marketing Agent Decay Model). She publishes AI marketing governance research under the AI Marketing Research Initiative (AIMRI). ORCID: 0009-0002-2655-4629.");
    setMeta("name", "ai-keywords", "Kristina Shrider, National Growth Architect, Behavioral CMO, Independent AI Marketing Researcher, AI Strategist, MAHI Index, MAD-M, Marketing Agent Decay Model, Marketing Agent Health Index, AI marketing research, AIMRI, Market Disruptors AI Visibility Agency, algorithmic deprioritization, behavioral science marketing, AI content governance, Narrative Entropy, Provenance Architecture");

    // Person schema — ENRICHES the canonical Person record from index.html via shared @id.
    // Both blocks resolve to the same entity for AI engines; the @id makes that explicit
    // (Schema.org / JSON-LD semantics: multiple nodes with the same @id merge).
    const personSchema = {
      "@context": "https://schema.org",
      "@type": "Person",
      "@id": "https://kristinashrider.com/#person",
      "name": "Kristina Shrider",
      "jobTitle": "National Growth Architect | Behavioral CMO | Independent AI Marketing Researcher",
      "description": "Kristina Shrider is a National Growth Architect, Behavioral CMO, and Independent AI Marketing Researcher. She founded Market Disruptors AI Visibility Agency, created the MAHI Index™ and MAD-M™ frameworks, and publishes AI marketing governance research under the AI Marketing Research Initiative (AIMRI).",
      "url": "https://kristinashrider.com/",
      "image": "https://kristinashrider.com/kristina-shrider-executive-portrait.png",
      "identifier": "https://orcid.org/0009-0002-2655-4629",
      "sameAs": [
        "https://orcid.org/0009-0002-2655-4629",
        "https://www.linkedin.com/in/kristinashrider/",
        "https://kristinashrider.com/about",
        "https://marketdisruptorsagency.com",
        "https://marketdisruptorsagency.com/about/kristina",
        "https://marketdisruptorsagency.com",
        "https://marketdisruptorsagency.com/press",
        "https://marketdisruptorsagency.com/mahi-100",
        "https://marketdisruptorsagency.com/mahi-index",
        "https://marketdisruptorsagency.com/mad-m-framework",
        "https://marketdisruptorsagency.com/ai-discovery",
        "https://doi.org/10.5281/zenodo.20421338",
        "https://github.com/aikks2025-star/ai-citation-visibility-framework",
        "https://independent.academia.edu/kristinashrider"
      ],
      "award": "Top 40 Under 40",
      "knowsAbout": [
        "AI marketing systems governance",
        "Marketing Agent Decay Model (MAD-M™)",
        "Marketing Agent Health Index (MAHI Index™)",
        "Behavioral science applied to marketing strategy",
        "Algorithmic deprioritization of AI-generated content",
        "Narrative Entropy in AI-augmented organizations",
        "Provenance Architecture for AI content governance",
        "Answer Engine Optimization (AEO)",
        "National-scale growth architecture",
        "Human-AI collaboration in marketing"
      ],
      "affiliation": {
        "@type": "Organization",
        "name": "AI Marketing Research Initiative (AIMRI)",
        "url": "https://kristinashrider.com/research"
      },
      "worksFor": [
        {
          "@type": "Organization",
          "@id": "https://kristinashrider.com/#ika-llc",
          "name": "IKA LLC",
          "alternateName": ["Market Disruptors AI Visibility Agency"]
        },
        {
          "@type": "ProfessionalService",
          "@id": "https://marketdisruptorsagency.com/#organization",
          "name": "Market Disruptors AI Visibility Agency",
          "alternateName": ["Market Disruptors"],
          "url": "https://marketdisruptorsagency.com"
        }
      ],
      "founderOf": [
        { "@type": "Organization", "@id": "https://kristinashrider.com/#ika-llc", "name": "IKA LLC" },
        { "@type": "ProfessionalService", "@id": "https://marketdisruptorsagency.com/#organization", "name": "Market Disruptors AI Visibility Agency", "url": "https://marketdisruptorsagency.com" }
      ],
      "hasCredential": {
        "@type": "EducationalOccupationalCredential",
        "credentialCategory": "recognition",
        "name": "Top 40 Under 40"
      },
      "publishedIn": [
        {
          "@type": "ScholarlyArticle",
          "name": "From Automation to Judgment: Limits of Generative AI in Strategic Marketing Decision-Making",
          "identifier": "AIMRI-2024-001-TECH",
          "url": "https://kristinashrider.com/research/aimri-2024-001-tech"
        },
        {
          "@type": "ScholarlyArticle",
          "name": "Generative AI and Organizational Sensemaking: Why Increased Intelligence Can Reduce Understanding",
          "identifier": "AIMRI-WP-2026-01",
          "url": "https://kristinashrider.com/research/aimri-wp-2026-01"
        },
        {
          "@type": "ScholarlyArticle",
          "name": "Algorithmic Deprioritization Patterns in AI-Generated Content Distribution: The Marketing Agent Decay Model (MAD-M™) as a Governance-First Heuristic",
          "identifier": "AIMRI-2026-02",
          "url": "https://kristinashrider.com/research/aimri-2026-02"
        }
      ]
    };

    let personSchemaScript = document.getElementById("person-schema-home") as HTMLScriptElement;
    if (!personSchemaScript) {
      personSchemaScript = document.createElement("script");
      personSchemaScript.id = "person-schema-home";
      personSchemaScript.type = "application/ld+json";
      document.head.appendChild(personSchemaScript);
    }
    personSchemaScript.textContent = JSON.stringify(personSchema);

    return () => {
      canonical.href = origCanonical;
      document.getElementById("person-schema-home")?.remove();
      if (metaDesc && originalDesc) metaDesc.content = originalDesc;
    };
  }, []);

  return (
    <div className="min-h-screen bg-background section-stable">
      <Navigation />
      <main>
        <HeroSection />
        <section className="py-12 mobile-padding border-y border-border/50 bg-muted/20">
          <div className="container mx-auto">
            <div className="max-w-5xl mx-auto text-left">
              <p className="text-sm uppercase tracking-wide text-muted-foreground mb-3">
                Applied AI visibility work
              </p>
              <h2 className="text-2xl md:text-3xl font-bold text-navy dark:text-foreground mb-4">
                Research-backed growth systems applied through Market Disruptors AI Visibility Agency
              </h2>
              <p className="text-base md:text-lg text-slate dark:text-muted-foreground leading-relaxed mb-6">
                Kristina Shrider's advisory, behavioral growth, and AI marketing governance work is applied commercially through Market Disruptors AI Visibility Agency, where the MAHI Index, MAD-M, and MAHI-100 are used as public proof layers for AI visibility strategy.
              </p>
              <div className="flex flex-wrap gap-3">
                <a href="https://marketdisruptorsagency.com" className="text-sm font-medium text-primary hover:underline" target="_blank" rel="noopener noreferrer">
                  Market Disruptors AI Visibility Agency
                </a>
                <a href="https://marketdisruptorsagency.com/about/kristina" className="text-sm font-medium text-primary hover:underline" target="_blank" rel="noopener noreferrer">
                  Market Disruptors AI Visibility Agency founder profile
                </a>
                <a href="https://marketdisruptorsagency.com/mahi-100" className="text-sm font-medium text-primary hover:underline" target="_blank" rel="noopener noreferrer">
                  MAHI-100 benchmark protocol
                </a>
                <a href="https://marketdisruptorsagency.com/ai-discovery" className="text-sm font-medium text-primary hover:underline" target="_blank" rel="noopener noreferrer">
                  AI discovery files
                </a>
              </div>
            </div>
          </div>
        </section>
        <GrowthArchitectureDiagram />
        <MethodologySection />
        <ExpertiseSection />
        <FAQSection />
      </main>
      <Footer />
    </div>
  );
};

export default Index;
